import { usePrivateFileControl } from '@modules/file-menagement';
import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import {
  LightboxProps as OriginalLightboxProps,
  PluginProps,
  Slide,
} from 'yet-another-react-lightbox';
import { CircularProgressWithLabel } from '../CircularProgressWithLabel';
import FileThumbnail from '../file-thumbnail';
import Iconify from '../iconify';

declare module 'yet-another-react-lightbox' {
  interface SlideTypes {
    privateFile: SlidePrivateFile;
  }
}

export interface SlidePrivateFile {
  type: 'private-file';
  id: string;
  name: string;
  mimeType: string;
}

export const defaultPdfProps = {
  controls: true,
};

interface LightboxProps extends OriginalLightboxProps {
  pdf?: typeof defaultPdfProps;
}

function isPrivateFileSlide(slide: Slide): slide is SlidePrivateFile {
  return slide.type === 'private-file';
}
export const resolvePdfProps = (pdf: LightboxProps['pdf']) => ({
  ...defaultPdfProps,
  ...pdf,
});

export function PrivateFile({ augment }: PluginProps) {
  augment(
    ({
      render: { slide: renderSlide, thumbnail: renderThumbnail, ...restRender },
      ...restProps
    }) => ({
      render: {
        thumbnail: ({ slide, ...rest }) => {
          if (!isPrivateFileSlide(slide))
            return renderThumbnail?.({ ...rest, slide });

          const { file, isLoading, progress, allowPreview } =
            usePrivateFileControl(
              {
                _id: slide.id,
                name: slide.name,
                mimeType: slide.mimeType,
              },
              100,
            );

          if (allowPreview) {
            if (isLoading)
              return <CircularProgressWithLabel value={progress} />;
            return <FileThumbnail file={file} imageView tooltip />;
          }

          return <FileThumbnail file={file} tooltip />;
        },
        slide: ({ slide, ...rest }) => {
          if (!isPrivateFileSlide(slide))
            return renderSlide ? renderSlide({ slide, ...rest }) : null;

          const {
            file,
            isLoading,
            progress,
            allowPreview,
            download,
            isInQueue,
          } = usePrivateFileControl(
            {
              _id: slide.id,
              name: slide.name,
              mimeType: slide.mimeType,
            },
            Infinity,
            true,
          );

          if (allowPreview) {
            if (isInQueue) return <CircularProgress size={32} />;
            if (isLoading)
              return <CircularProgressWithLabel value={progress} />;
            return (
              <FileThumbnail
                key={slide.id}
                file={file}
                imageView
                sx={{
                  maxWidth: '90vw',
                  overflow: 'hidden',
                  maxHeight: '90vh',
                }}
                imgSx={{
                  objectFit: 'contain',
                  maxHeight: 'inherit',
                }}
              />
            );
          }

          return (
            <Stack alignItems="center" gap={4}>
              <FileThumbnail
                key={slide.id}
                file={file}
                imgSx={{ width: 128, height: 128 }}
                sx={{ justifyContent: 'center' }}
              />
              <Typography variant="body2" color="common.white">
                {file.name}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="large"
                disabled={isLoading}
                onClick={() => {
                  download();
                }}
                startIcon={
                  isLoading ? (
                    <CircularProgressWithLabel size={32} value={progress} />
                  ) : (
                    <Iconify icon="carbon:download" />
                  )
                }
              >
                Download
              </Button>
            </Stack>
          );
        },
        ...restRender,
      },
      ...restProps,
    }),
  );
}
